import React, { useEffect, useMemo, useState } from 'react';
import { clearToolChainWaitRequest, resumeToolChainWait } from '../utils/api';
import './ConversationIngestModal.css';

/**
 * Pause UI when a tool_chain step is marked interactive.
 * User can confirm, edit the handoff JSON/summary, add feedback, or cancel.
 */
export default function ToolChainWaitModal({
  open,
  onClose,
  sessionId,
  waitRequest = null,
  onResumed = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [handoffText, setHandoffText] = useState('');

  const title = useMemo(() => {
    const slug = waitRequest?.chain_tool_slug || 'tool chain';
    const step = Number(waitRequest?.step_index);
    const total = Number(waitRequest?.steps_total);
    if (Number.isFinite(step) && Number.isFinite(total) && total > 0) {
      return `Tool chain pause · ${slug} · step ${step + 1}/${total}`;
    }
    return `Tool chain pause · ${slug}`;
  }, [waitRequest]);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setError('');
      setFeedback('');
      setHandoffText('');
      return;
    }
    setError('');
    setFeedback('');
    try {
      setHandoffText(JSON.stringify(waitRequest?.previous_result || {}, null, 2));
    } catch (_) {
      setHandoffText(String(waitRequest?.previous_result?.summary || ''));
    }
  }, [open, waitRequest]);

  if (!open) return null;

  const handleCancel = async () => {
    setBusy(true);
    setError('');
    try {
      if (sessionId) {
        try { await clearToolChainWaitRequest(sessionId); } catch (_) { /* ignore */ }
      }
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError('');
    try {
      let previous_result = waitRequest?.previous_result || null;
      const raw = String(handoffText || '').trim();
      if (raw) {
        try {
          previous_result = JSON.parse(raw);
        } catch (_) {
          previous_result = {
            ...(previous_result && typeof previous_result === 'object' ? previous_result : {}),
            summary: raw,
            success: true,
            items: Array.isArray(previous_result?.items) ? previous_result.items : [],
          };
        }
      }
      const data = await resumeToolChainWait({
        sessionId,
        previous_result,
        feedback: feedback.trim() || null,
      });
      onResumed?.(data);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to continue tool chain');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="conversation-ingest-overlay" role="dialog" aria-modal="true" aria-labelledby="tool-chain-wait-title">
      <div className="conversation-ingest-modal" style={{ width: 'min(640px, 94vw)', minHeight: 'auto' }}>
        <div className="conversation-ingest-header">
          <h3 id="tool-chain-wait-title">{title}</h3>
          <p className="conversation-ingest-hint" style={{ margin: '6px 0 0' }}>
            An interactive chain step finished. Confirm or edit the handoff payload, optionally add feedback,
            then continue to the next tool.
          </p>
        </div>
        <div className="conversation-ingest-body">
          {error ? <p className="conversation-ingest-error" role="alert">{error}</p> : null}
          <label className="conversation-ingest-hint" style={{ display: 'block' }}>
            Handoff (JSON or summary text)
            <textarea
              value={handoffText}
              onChange={(e) => setHandoffText(e.target.value)}
              rows={12}
              disabled={busy}
              style={{ width: '100%', marginTop: 6, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}
            />
          </label>
          <label className="conversation-ingest-hint" style={{ display: 'block', marginTop: 12 }}>
            Feedback for next step (optional)
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              disabled={busy}
              style={{ width: '100%', marginTop: 6, resize: 'vertical' }}
              placeholder="Notes the next tool should take into account…"
            />
          </label>
        </div>
        <div className="conversation-ingest-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={handleCancel} disabled={busy}>
            Cancel chain
          </button>
          <button type="button" className="btn" onClick={handleContinue} disabled={busy}>
            {busy ? 'Continuing…' : 'Continue chain'}
          </button>
        </div>
      </div>
    </div>
  );
}
